import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, Download, KeyRound, Lock, RotateCcw, ShieldCheck, ShieldOff, Smartphone, Users } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";
import { PageHeader, useTenant } from "@/components/AppShell";
import { Badge, Button, Card, cx, Input, Skeleton } from "@/components/ui";
import { api, ApiError, canManage } from "@/lib/api";
import { fmtRelative } from "@/lib/format";

import { t } from "@/lib/i18n";
const errMsg = (e: unknown, fallback: string) => (e instanceof ApiError ? ((e.body as { message?: string })?.message ?? fallback) : fallback);

/** Einrichtung der Zwei-Faktor-Anmeldung: QR-Code scannen, Code bestätigen, Wiederherstellungscodes sichern */
export function MfaSetupWizard({ onDone, onCancel }: { onDone: () => void; onCancel?: () => void }) {
  const [codes, setCodes] = useState<string[] | null>(null);
  const [code, setCode] = useState("");
  const [qr, setQr] = useState<string | null>(null);
  const [showSecret, setShowSecret] = useState(false);
  const setup = useMutation({ mutationFn: api.security.setup });
  useEffect(() => {
    setup.mutate(undefined, {
      onSuccess: async (r) => {
        const QR = (await import("qrcode")).default;
        setQr(await QR.toDataURL(r.otpauthUrl, { margin: 1, width: 200 }));
      },
      onError: (e) => toast.error(errMsg(e, "Einrichtung konnte nicht gestartet werden")),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const enable = useMutation({
    mutationFn: () => api.security.enable(code),
    onSuccess: (r) => setCodes(r.recoveryCodes),
    onError: (e) => toast.error(errMsg(e, "Der Code stimmt nicht")),
  });

  if (codes) return <RecoveryCodes codes={codes} onDone={onDone} />;
  return (
    <div className="space-y-4">
      <ol className="space-y-4 text-[13px]">
        <li>
          <p className="font-medium">{t("1. Authenticator-App öffnen")}</p>
          <p className="text-fg-muted">{t("z. B. Google Authenticator, Microsoft Authenticator, 1Password oder Authy – auf dem Handy oder im Passwort-Manager.")}</p>
        </li>
        <li>
          <p className="font-medium">{t("2. QR-Code scannen")}</p>
          <div className="mt-2 flex items-start gap-4">
            <div className="size-[164px] shrink-0 rounded-xl border border-border bg-white grid place-items-center overflow-hidden">
              {qr ? <img src={qr} alt={t("QR-Code für die Authenticator-App")} className="size-[156px]" /> : <Skeleton className="size-[140px]" />}
            </div>
            <div className="text-fg-muted text-[12.5px]">
              {t("Kein Scannen möglich?")}{" "}
              <button className="underline underline-offset-2 hover:text-fg" onClick={() => setShowSecret((s) => !s)}>{t("Schlüssel anzeigen")}</button>
              {showSecret && setup.data && <p className="mt-2 font-mono text-[13px] text-fg break-all select-all">{setup.data.secret.replace(/(.{4})/g, "$1 ").trim()}</p>}
            </div>
          </div>
        </li>
        <li>
          <p className="font-medium">{t("3. Angezeigten Code eingeben")}</p>
          <form className="mt-2 flex gap-2" onSubmit={(e) => { e.preventDefault(); if (code.replace(/\s/g, "").length === 6) enable.mutate(); }}>
            <Input value={code} onChange={(e) => setCode(e.target.value.replace(/[^\d ]/g, "").slice(0, 7))} placeholder="123 456" inputMode="numeric" autoComplete="one-time-code" aria-label={t("6-stelliger Code")} className="w-36 h-10 text-[18px] tracking-[0.2em] font-mono" autoFocus />
            <Button variant="primary" className="h-10" loading={enable.isPending} disabled={code.replace(/\s/g, "").length !== 6 || !setup.data}>{t("Bestätigen")}</Button>
            {onCancel && <Button type="button" className="h-10" variant="ghost" onClick={onCancel}>{t("Abbrechen")}</Button>}
          </form>
        </li>
      </ol>
    </div>
  );
}

function RecoveryCodes({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const [saved, setSaved] = useState(false);
  const text = `Logiful Connect – Wiederherstellungscodes\nJeder Code gilt einmal, falls die Authenticator-App nicht verfügbar ist.\n\n${codes.join("\n")}\n`;
  const download = () => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([text], { type: "text/plain" }));
    a.download = "logiful-wiederherstellungscodes.txt";
    a.click();
    URL.revokeObjectURL(a.href);
    setSaved(true);
  };
  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-success/25 bg-success-soft/60 px-4 py-3 text-[13px] flex items-center gap-2"><ShieldCheck className="size-4 text-success" /> {t("Zwei-Faktor-Anmeldung ist aktiv.")}</div>
      <div>
        <p className="font-medium text-[13.5px]">{t("Wiederherstellungscodes sichern")}</p>
        <p className="text-[12.5px] text-fg-muted">{t("Falls das Handy weg ist: Jeder Code ersetzt einmal den App-Code. Sie werden nur jetzt angezeigt – bitte im Passwort-Manager oder ausgedruckt aufbewahren.")}</p>
      </div>
      <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 rounded-xl border border-border bg-surface-2/60 px-4 py-3 font-mono text-[14px]">
        {codes.map((c) => <span key={c} className="select-all">{c}</span>)}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" icon={<Download className="size-3.5" />} onClick={download}>{t("Als Datei speichern")}</Button>
        <Button size="sm" icon={<Copy className="size-3.5" />} onClick={() => { void navigator.clipboard.writeText(text); setSaved(true); toast.success("Kopiert"); }}>{t("Kopieren")}</Button>
        <Button size="sm" variant="primary" className="ml-auto" disabled={!saved} onClick={onDone} title={saved ? undefined : t("Bitte zuerst speichern oder kopieren")}>
          <Check className="size-3.5" /> {t("Gesichert – fertig")}
        </Button>
      </div>
    </div>
  );
}

/** Code abfragen (Ausschalten, neue Codes) */
function CodePrompt({ label, cta, danger, onSubmit, busy, onCancel }: { label: string; cta: string; danger?: boolean; onSubmit: (code: string) => void; busy: boolean; onCancel: () => void }) {
  const [code, setCode] = useState("");
  return (
    <form className="mt-3 flex flex-wrap items-center gap-2" onSubmit={(e) => { e.preventDefault(); onSubmit(code); }}>
      <span className="text-[12.5px] text-fg-muted w-full">{t(label)}</span>
      <Input value={code} onChange={(e) => setCode(e.target.value.slice(0, 12))} placeholder={t("Code aus der App")} autoFocus autoComplete="one-time-code" className="w-44" />
      <Button size="sm" variant={danger ? "danger" : "primary"} loading={busy} disabled={code.trim().length < 6}>{cta}</Button>
      <Button size="sm" type="button" variant="ghost" onClick={onCancel}>{t("Abbrechen")}</Button>
    </form>
  );
}

function MyMfaCard() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["mfa"], queryFn: api.security.mfa });
  const [mode, setMode] = useState<"idle" | "setup" | "disable" | "renew">("idle");
  const [codes, setCodes] = useState<string[] | null>(null);
  const refresh = () => { void qc.invalidateQueries({ queryKey: ["mfa"] }); void qc.invalidateQueries({ queryKey: ["me"] }); void qc.invalidateQueries({ queryKey: ["team-security"] }); };
  const disable = useMutation({ mutationFn: (c: string) => api.security.disable(c), onSuccess: () => { toast.success("Zwei-Faktor-Anmeldung ausgeschaltet"); setMode("idle"); refresh(); }, onError: (e) => toast.error(errMsg(e, "Der Code stimmt nicht")) });
  const renew = useMutation({ mutationFn: (c: string) => api.security.renewCodes(c), onSuccess: (r) => { setCodes(r.recoveryCodes); setMode("idle"); refresh(); }, onError: (e) => toast.error(errMsg(e, "Der Code stimmt nicht")) });
  const d = q.data;
  return (
    <Card className="p-5">
      <div className="flex flex-wrap items-start gap-3">
        <span className={cx("size-9 rounded-xl grid place-items-center", d?.enabled ? "bg-success-soft text-success" : "bg-surface-2 text-fg-muted")}><Smartphone className="size-4" /></span>
        <div className="min-w-0 flex-1">
          <h2 className="font-semibold flex items-center gap-2">{t("Zwei-Faktor-Anmeldung")} {d && (d.enabled ? <Badge tone="success">{t("aktiv")}</Badge> : <Badge>{t("aus")}</Badge>)}{d?.required && <Badge tone="info">{t("vorgeschrieben")}</Badge>}</h2>
          <p className="text-[12.5px] text-fg-muted mt-0.5">{t("Zusätzlich zum E-Mail-Link ein Code aus einer Authenticator-App – schützt euer Konto, auch wenn jemand Zugriff auf das E-Mail-Postfach bekommt.")}</p>
          {d?.enabled && <p className="text-[12px] text-fg-subtle mt-1">{t("Eingerichtet")} {fmtRelative(d.enabledAt)} · {d.recoveryRemaining} {t("Wiederherstellungscodes übrig")}</p>}
        </div>
        {d && !d.enabled && mode !== "setup" && <Button variant="primary" icon={<ShieldCheck className="size-4" />} onClick={() => setMode("setup")}>{t("Einrichten")}</Button>}
        {d?.enabled && mode === "idle" && !codes && (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" icon={<KeyRound className="size-3.5" />} onClick={() => setMode("renew")}>{t("Neue Codes")}</Button>
            {!d.required && <Button size="sm" variant="ghost" icon={<ShieldOff className="size-3.5" />} onClick={() => setMode("disable")}>{t("Ausschalten")}</Button>}
          </div>
        )}
      </div>
      {!d && <Skeleton className="h-10 w-full mt-3" />}
      {mode === "setup" && <div className="mt-5 border-t border-border pt-5"><MfaSetupWizard onDone={() => { setMode("idle"); refresh(); toast.success("Zwei-Faktor-Anmeldung eingerichtet"); }} onCancel={() => setMode("idle")} /></div>}
      {mode === "disable" && <CodePrompt label={t("Zum Ausschalten den aktuellen Code aus der App (oder einen Wiederherstellungscode) eingeben:")} cta={t("Ausschalten")} danger busy={disable.isPending} onSubmit={(c) => disable.mutate(c)} onCancel={() => setMode("idle")} />}
      {mode === "renew" && <CodePrompt label={t("Neue Wiederherstellungscodes erzeugen – die bisherigen werden ungültig. Aktuellen Code aus der App eingeben:")} cta={t("Neue Codes erzeugen")} busy={renew.isPending} onSubmit={(c) => renew.mutate(c)} onCancel={() => setMode("idle")} />}
      {codes && <div className="mt-5 border-t border-border pt-5"><RecoveryCodes codes={codes} onDone={() => setCodes(null)} /></div>}
    </Card>
  );
}

function TeamSecurityCard() {
  const { tenantId, tenant, role, me } = useTenant();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["team-security", tenantId], queryFn: () => api.security.team(tenantId) });
  const admin = canManage(role);
  const set = useMutation({
    mutationFn: (v: boolean) => api.security.setRequireMfa(tenantId, v),
    onSuccess: (r) => { toast.success(r.requireMfa ? "Zwei-Faktor ist jetzt für alle vorgeschrieben" : "Zwei-Faktor nicht mehr vorgeschrieben"); void qc.invalidateQueries({ queryKey: ["team-security", tenantId] }); },
    onError: (e) => toast.error(errMsg(e, "Konnte nicht gespeichert werden")),
  });
  const reset = useMutation({
    mutationFn: (userId: string) => api.security.resetMember(tenantId, userId),
    onSuccess: () => { toast.success("Zurückgesetzt – die Person meldet sich neu an und richtet die App neu ein"); void qc.invalidateQueries({ queryKey: ["team-security", tenantId] }); },
    onError: (e) => toast.error(errMsg(e, "Zurücksetzen fehlgeschlagen")),
  });
  const d = q.data;
  const without = d?.members.filter((m) => !m.mfa).length ?? 0;
  return (
    <Card className="overflow-hidden">
      <div className="p-5 flex flex-wrap items-start gap-3">
        <span className="size-9 rounded-xl grid place-items-center bg-surface-2 text-fg-muted"><Users className="size-4" /></span>
        <div className="min-w-0 flex-1">
          <h2 className="font-semibold">{t("Team von")} {tenant.name}</h2>
          <p className="text-[12.5px] text-fg-muted mt-0.5">{d ? (without ? t("{without} von {length} ohne Zwei-Faktor-Anmeldung.", { without, length: d.members.length }) : t("Alle nutzen die Zwei-Faktor-Anmeldung.")) : " "}</p>
        </div>
        {admin && d && (
          <label className="inline-flex items-center gap-2 text-[13px] cursor-pointer">
            <input type="checkbox" checked={d.requireMfa} disabled={set.isPending} onChange={(e) => { if (!e.target.checked || confirm("Zwei-Faktor-Anmeldung für alle vorschreiben? Wer sie noch nicht nutzt, muss sie bei der nächsten Aktion einrichten.")) set.mutate(e.target.checked); }} className="accent-ink" />
            {t("Für alle vorschreiben")}
          </label>
        )}
      </div>
      <ul className="divide-y divide-border border-t border-border">
        {!d && <li className="px-5 py-3"><Skeleton className="h-4 w-1/2" /></li>}
        {d?.members.map((m) => (
          <li key={m.userId} className="px-5 py-2.5 flex flex-wrap items-center gap-3 text-[13px]">
            <span className="min-w-0 flex-1">
              <span className="font-medium">{m.name ?? m.email}</span>{m.name && <span className="text-fg-muted"> · {m.email}</span>}
              <span className="block text-[11.5px] text-fg-subtle">{m.role === "customer_admin" ? t("Administrator") : t("Mitarbeiter")} {t("· zuletzt angemeldet")} {fmtRelative(m.lastLoginAt)}</span>
            </span>
            {m.mfa ? <Badge tone="success" icon={<Lock className="size-3" />}>{t("2FA aktiv")}</Badge> : <Badge tone={d.requireMfa ? "warning" : "neutral"}>{d.requireMfa ? t("muss einrichten") : t("ohne 2FA")}</Badge>}
            {admin && m.mfa && m.userId !== me.user.id && (
              <Button size="sm" variant="ghost" icon={<RotateCcw className="size-3.5" />} loading={reset.isPending && reset.variables === m.userId} onClick={() => confirm(`Zwei-Faktor von ${m.email} zurücksetzen? Die Person wird abgemeldet und richtet die App neu ein.`) && reset.mutate(m.userId)}>{t("Zurücksetzen")}</Button>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}

export function SecurityPage() {
  const { role } = useTenant();
  return (
    <div className="pb-16">
      <PageHeader title={t("Sicherheit")} description={t("Anmeldung absichern und nachvollziehen, wer was geändert hat.")} actions={canManage(role) ? <Link to="/settings/audit"><Button>{t("Änderungsprotokoll")}</Button></Link> : undefined} />
      <div className="px-4 md:px-8 space-y-4 max-w-3xl">
        <MyMfaCard />
        <TeamSecurityCard />
      </div>
    </div>
  );
}
